import { faPlus } from '@fortawesome/pro-solid-svg-icons'
import { useApprovalDomainApprovers } from '@hooks/useApprovalDomainApprovers'
import { useDepartments } from '@hooks/useDepartments'
import { useFacilities } from '@hooks/useFacilities'
import { Tooltip } from '@lib/tooltip'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { Button } from 'components/buttons'
import { SimpleTable } from 'components/tables'
import { ReactNode, useId, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TableButton } from './TableButton'

const columnHelper = createColumnHelper<Approver>()

const TruncateWithEllipsis = ({ children }: { children: ReactNode }) => {
  const id = `truncate-tooltip-${useId()}`
  return (
    <>
      <div
        data-tooltip-id={id}
        className="max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap"
      >
        {children}
      </div>
      <Tooltip id={id}>{children}</Tooltip>
    </>
  )
}

export const ApproversTable = ({
  approvalDomainId,
  onAdd,
  onEdit,
  onDelete,
}: {
  approvalDomainId: string
  onAdd: () => void
  onEdit: (approver: Approver) => void
  onDelete: (approver: Approver) => void
}) => {
  const { t } = useTranslation()
  const { data: facilities = [] } = useFacilities()
  const { data: departments = [] } = useDepartments()
  const { data: approvers = [] } = useApprovalDomainApprovers(approvalDomainId)

  const facilitiesById = useMemo(
    () => new Map(facilities.map((f) => [f.id, f])),
    [facilities],
  )

  const departmentsById = useMemo(
    () => new Map(departments.map((d) => [d.id, d])),
    [departments],
  )

  const renderFacilityNames = (approver: Approver) => {
    if (approver.allFacilities) return t('common.all')

    return approver.facilityIds
      .map((id) => facilitiesById.get(id)?.name ?? t('common.idPrefix', { id }))
      .join(', ')
  }

  const renderDepartmentNames = (approver: Approver) => {
    if (approver.allWorkdayDepartments) return t('common.all')

    return approver.workdayDepartmentIds
      .map(
        (id) => departmentsById.get(id)?.name ?? t('common.idPrefix', { id }),
      )
      .join(', ')
  }

  const columns = [
    columnHelper.accessor('user.name', {
      header: t('common.name'),
      id: 'name',
      cell: ({ getValue }) => (
        <TruncateWithEllipsis>{getValue()}</TruncateWithEllipsis>
      ),
    }),
    columnHelper.accessor('user.email', {
      header: t('common.email'),
      id: 'email',
      cell: ({ getValue }) => (
        <TruncateWithEllipsis>{getValue()}</TruncateWithEllipsis>
      ),
    }),
    columnHelper.accessor(renderFacilityNames, {
      id: 'facilities',
      header: t('common.facilities'),
      cell: ({ getValue }) => (
        <TruncateWithEllipsis>{getValue()}</TruncateWithEllipsis>
      ),
    }),
    columnHelper.accessor(renderDepartmentNames, {
      id: 'departments',
      header: t(
        'features.approvalGroupsAdmin.approvalGroupDetails.departments',
      ),
      cell: ({ getValue }) => (
        <TruncateWithEllipsis>{getValue()}</TruncateWithEllipsis>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: t('features.approvalGroupsAdmin.approvalGroupDetails.actions'),
      cell: ({ row }) => {
        const approver = row.original
        return (
          <div className="flex gap-2 items-center">
            <TableButton variant="primary" onClick={() => onEdit(approver)}>
              {t('common.edit')}
            </TableButton>
            |
            <TableButton variant="danger" onClick={() => onDelete(approver)}>
              {t('common.delete')}
            </TableButton>
          </div>
        )
      },
    }),
  ] as ColumnDef<Approver>[]

  return (
    <div className="flex flex-col w-[825px]">
      {approvers.length > 0 ? (
        <SimpleTable
          columns={columns}
          data={approvers}
          defaultSortColumn="name"
        />
      ) : (
        <div className="flex flex-col items-center text-sm gap-4 py-8 text-neutral-700 rounded text-center border border-neutral-200">
          {t('features.approvalGroupsAdmin.approvalGroupDetails.noApprovers')}
          <Button icon={faPlus} className="text-sm" onClick={onAdd}>
            {t('features.approvalGroupsAdmin.approvalGroupDetails.addApprover')}
          </Button>
        </div>
      )}
    </div>
  )
}
